import { TERM_KINDS, type TermKind } from '@zyx/shared';
import { Heart } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/page-header';
import { EmptyState, ErrorState, LoadingRows } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PersonPhoto } from '@/components/movie/person-photo';
import { usePeople, useTerms } from '@/hooks/use-movies';
import { urls } from '@/lib/urls';
import { TERM_KIND_LABELS } from './term-page';

const PLURALS: Readonly<Record<TermKind, string>> = { genre: 'Genres', maker: 'Makers', series: 'Series', label: 'Labels', tag: 'Tags' };

/** The tab for people; the others are term kinds. */
const ACTRESSES = 'actresses';

/** Actresses, genres, makers, series, labels and tags, most movies first: what the library holds. */
export function CollectionsPage() {
  const navigate = useNavigate();
  const params = useParams();
  const tab = params['kind'] === ACTRESSES ? ACTRESSES : (TERM_KINDS.find((candidate) => candidate === params['kind']) ?? 'genre');
  const [filter, setFilter] = useState('');
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const q = filter.trim() === '' ? undefined : filter.trim();
  const label = tab === ACTRESSES ? 'Actresses' : PLURALS[tab];

  return (
    <div className="space-y-6">
      <PageHeader title="Collections" actions={<Input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder={`Filter ${label.toLowerCase()}…`} className="w-56" />} />
      <Tabs value={tab} onValueChange={(value) => navigate(value === ACTRESSES ? urls.actresses() : urls.terms(TERM_KINDS.find((candidate) => candidate === value) ?? 'genre'))}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value={ACTRESSES}>Actresses</TabsTrigger>
          {TERM_KINDS.map((candidate) => (
            <TabsTrigger key={candidate} value={candidate}>
              {PLURALS[candidate]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {tab === ACTRESSES ? (
        <div className="space-y-4">
          <div className="flex gap-1" role="group" aria-label="Which actresses">
            <Button size="sm" variant={favouritesOnly ? 'ghost' : 'secondary'} aria-pressed={!favouritesOnly} onClick={() => setFavouritesOnly(false)}>
              With movies
            </Button>
            <Button size="sm" variant={favouritesOnly ? 'secondary' : 'ghost'} aria-pressed={favouritesOnly} onClick={() => setFavouritesOnly(true)}>
              <Heart />
              Favourites
            </Button>
          </div>
          <Actresses q={q} favourites={favouritesOnly} />
        </div>
      ) : (
        <Terms kind={tab} q={q} />
      )}
    </div>
  );
}

/** People with movies in the library (or only favourites), each with her photo, most movies first. */
export function Actresses(props: { readonly q?: string; readonly favourites?: boolean }) {
  const people = usePeople({ q: props.q, favourite: props.favourites, limit: 300 });
  if (people.isPending) {
    return <LoadingRows rows={6} />;
  }
  if (people.isError) {
    return <ErrorState error={people.error} onRetry={() => void people.refetch()} />;
  }
  // An older service ignores `favourite` and sends everyone; keep only real favourites.
  const shown = props.favourites === true ? people.data.people.filter((person) => person.favourite === true) : people.data.people;
  if (shown.length === 0) {
    return props.favourites === true ? (
      <EmptyState title="No favourite actresses yet" description="The heart on an actress's page adds her here." icon={<Heart className="size-5" />} />
    ) : (
      <EmptyState title="No actresses yet" description="Actresses appear as movies with them are indexed." />
    );
  }
  return (
    <div className="grid grid-cols-3 gap-x-3 gap-y-5 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10">
      {shown.map((person) => (
        <Link key={person.personId} to={urls.person(person.personId)} className="group flex flex-col items-center gap-1.5 text-center focus-visible:outline-none">
          <span className="relative">
            <PersonPhoto
              name={person.name}
              photo={person.photo}
              className="size-20 text-xl ring-offset-background transition-theme group-hover:ring-2 group-hover:ring-primary group-focus-visible:ring-2 group-focus-visible:ring-ring"
            />
            {person.favourite ? (
              <span className="absolute -right-0.5 bottom-0 rounded-full bg-background p-1 shadow-card">
                <Heart className="size-3.5 fill-primary text-primary" aria-hidden />
                <span className="sr-only">Favourite</span>
              </span>
            ) : null}
          </span>
          <span className="line-clamp-2 text-xs leading-snug group-hover:text-primary">{person.name}</span>
          <span className="text-[11px] text-muted-foreground">{person.movieCount}</span>
        </Link>
      ))}
    </div>
  );
}

function Terms(props: { readonly kind: TermKind; readonly q?: string }) {
  const { kind } = props;
  const terms = useTerms({ kind, q: props.q, limit: 300 });
  return terms.isPending ? (
    <LoadingRows rows={6} />
  ) : terms.isError ? (
    <ErrorState error={terms.error} onRetry={() => void terms.refetch()} />
  ) : terms.data.terms.length === 0 ? (
    <EmptyState title={`No ${PLURALS[kind].toLowerCase()} yet`} description={`${TERM_KIND_LABELS[kind]}s appear as movies are indexed.`} />
  ) : (
    <div className="flex flex-wrap gap-2">
      {terms.data.terms.map((term) => (
        <Link
          key={term.termId}
          to={urls.term(term.termId)}
          className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-sm transition-colors hover:border-primary hover:text-primary"
        >
          {term.name}
          <span className="text-xs text-muted-foreground">{term.movieCount}</span>
        </Link>
      ))}
    </div>
  );
}
